import { useArcadeStoreState } from '@src/hooks/useArcadeStoreState';
import {
  useGiveawayPrize,
  useGiveawayTotalEntriesQuery,
  useGiveawayUserEntriesQuery,
  useGiveawayUtils,
} from '@src/hooks/useGiveaways';
import { RaffleV2, shortenAddress } from 'jigsaw';
import { Route as reward_route } from '@routes/reward';
import { Route as giveaway_route } from '@routes/giveaways/$programId/index';
import {
  Button,
  buttonVariants,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  HStack,
  Skeleton,
  Text,
  VStack,
} from 'ui_2';

export const GiveawayClosed = ({ giveaway }: { giveaway: RaffleV2 }) => {
  const { isGiveawayClosed, isGiveawayOver, isGiveawayWinner, isGiveawayFull } =
    useGiveawayUtils(giveaway);

  return (
    <VStack>
      <Text font='light-sm'>Results</Text>
      <div className='rounded-lg border border-gray-100 p-4'>
        <VStack className='h-full items-center gap-4'>
          {!isGiveawayClosed && isGiveawayFull && (
            <VStack className='w-full rounded-lg border border-gray-100 text-center'>
              <Text font='medium-sm'>Giveaway Full</Text>
              <Text font='sm'>Giveaway will reset once winners are drawn</Text>
            </VStack>
          )}
          {isGiveawayOver === false && isGiveawayClosed && (
            <VStack className='w-full rounded-lg border border-gray-100 text-center'>
              <Text font='medium-sm'>Giveaway Closed</Text>
              <Text font='sm'>Winners will be drawn soon</Text>
            </VStack>
          )}
          {isGiveawayWinner && <YouWon giveaway={giveaway} />}
          {isGiveawayWinner === false &&
            giveaway.winnerAddresses.length > 0 && <YouLost />}

          <UserEntries giveaway={giveaway} />
          <TotalEntries giveaway={giveaway} />

          <WinnersView giveaway={giveaway} />
        </VStack>
      </div>
    </VStack>
  );
};

const UserEntries = ({ giveaway }: { giveaway: RaffleV2 }) => {
  const { data: userEntries, isLoading } =
    useGiveawayUserEntriesQuery(giveaway);
  const { isConnected } = useArcadeStoreState();

  if (!isConnected) return;

  if (isLoading) {
    return <Skeleton className='h-5 w-full' />;
  }

  if ((userEntries ?? 0) > 0) {
    return (
      <HStack className='w-full items-center justify-between'>
        <Text font='medium-sm'>Your entries</Text>
        <Text font='medium-sm'>{userEntries}</Text>
      </HStack>
    );
  }
};

const TotalEntries = ({ giveaway }: { giveaway: RaffleV2 }) => {
  const { data: totalEntries, isLoading } =
    useGiveawayTotalEntriesQuery(giveaway);

  if (isLoading) {
    return <Skeleton className='h-5 w-full' delay={1} />;
  }

  if ((totalEntries ?? 0) > 0) {
    return (
      <HStack className='w-full items-center justify-between'>
        <Text font='medium-sm'>Total entries</Text>
        <Text font='medium-sm'>{totalEntries}</Text>
      </HStack>
    );
  }
};

const WinnersView = ({ giveaway }: { giveaway: RaffleV2 }) => {
  const { account } = useArcadeStoreState();

  const winners = giveaway.winnerAddresses;

  if (winners.length === 0) {
    return undefined;
  }

  return (
    <HStack className='w-full items-center justify-between'>
      <Text font='medium-sm'>Winners</Text>
      <Dialog>
        <DialogTrigger
          className={buttonVariants({ variant: 'gray', size: 'md' })}
        >
          View Winners
        </DialogTrigger>
        <DialogContent className='h-full max-h-[min(70%,550px)] max-w-[calc(min(80%,500px))] overflow-y-auto'>
          <VStack className='gap-8'>
            <DialogHeader>
              <VStack className='h-auto items-center'>
                <DialogTitle>
                  <Text font='medium-md'>Winners</Text>
                </DialogTitle>
                <DialogDescription>
                  <Text font='sm'>
                    {winners.length} {winners.length > 1 ? 'Winners' : 'Winner'}{' '}
                    for {giveaway.title}
                  </Text>
                </DialogDescription>
              </VStack>
            </DialogHeader>
            <VStack>
              {winners.map((winner, ix) => (
                <HStack className='justify-center gap-2' key={ix}>
                  <Text font='medium-sm'>
                    {shortenAddress(winner, true, 8, false)}
                  </Text>
                  {winner === account?.address && (
                    <Text className='rounded-full bg-green-200 px-2 text-green-600'>
                      You
                    </Text>
                  )}
                </HStack>
              ))}
            </VStack>
          </VStack>
        </DialogContent>
      </Dialog>
    </HStack>
  );
};

const YouWon = ({ giveaway }: { giveaway: RaffleV2 }) => {
  const navigate = giveaway_route.useNavigate();
  const { data } = useGiveawayPrize(giveaway);
  const { isPuzzleWalletWebview } = useArcadeStoreState();

  return (
    <VStack className='w-full gap-3 p-2'>
      <VStack className='items-center rounded-[5px] bg-purple-100 py-2'>
        <Text color='purple' font='lg'>
          You Won!
        </Text>
      </VStack>
      <Button
        disabled={!data}
        onClick={() => {
          if (!data) return;
          navigate({
            to: reward_route.to,
            search: {
              id: data._id,
              skuTitle: data.productSKU.title,
              showHeader: isPuzzleWalletWebview ? false : true,
              showAccountAndBalance: isPuzzleWalletWebview ? false : true,
            },
          });
        }}
      >
        View your winnings
      </Button>
    </VStack>
  );
};

const YouLost = () => {
  return (
    <VStack className='w-full gap-3'>
      <VStack className='items-center rounded-[5px] bg-gray-100 p-2'>
        <Text font='lg'>You Lost!</Text>
      </VStack>
    </VStack>
  );
};
